// ===== frontend/src/components/Form/DynamicFormRenderer.jsx =====
import React, { useState, useEffect } from 'react';
import FormSection from './FormSection';
import FormProgress from './FormProgress';
import AIFillPanel from '../AI/AIFillPanel';
import { useFormData } from '../../hooks/useFormData';
import { useFieldVisibility } from '../../hooks/useFieldVisibility';
import { generateFilledPDF, downloadPDF } from '../../utils/api';

const DynamicFormRenderer = ({ sessionId }) => {
  const {
    formData,
    formStructure,
    isLoading,
    error,
    hasUnsavedChanges,
    updateField,
    updateFields,
    saveFormData
  } = useFormData(sessionId);

  const visibleFieldIds = useFieldVisibility(formStructure, formData);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'error'

  // Auto-save indicator
  useEffect(() => {
    if (hasUnsavedChanges) {
      setSaveStatus('saving');
      const timer = setTimeout(() => {
        setSaveStatus('saved');
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setSaveStatus('saved');
    }
  }, [hasUnsavedChanges]);

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formStructure) return errors;

    formStructure.sections.forEach(section => {
      section.fields.forEach(field => {
        if (!visibleFieldIds.has(field.id)) return;

        const value = formData[field.id];

        // Required field validation
        if (field.required && (!value || value.toString().trim() === '')) {
          errors[field.id] = 'This field is required';
        }

        // Pattern validation
        if (value && field.validation?.pattern) {
          const regex = new RegExp(field.validation.pattern);
          if (!regex.test(value)) {
            errors[field.id] = field.validation.patternMessage || 'Invalid format';
          }
        }

        // Length validation
        if (value && field.validation?.minLength && value.length < field.validation.minLength) {
          errors[field.id] = field.validation.minLengthMessage || `At least ${field.validation.minLength} characters required`;
        }
      });
    });

    setValidationErrors(errors);
    return errors;
  };

  // Handle field changes
  const handleFieldChange = (fieldId, value) => {
    updateField(fieldId, value);
    
    // Clear validation error for this field
    if (validationErrors[fieldId]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  // Handle AI fill completion
  const handleAIFillComplete = (filledData) => {
    updateFields(filledData);
    setShowAIPanel(false);
  };

  // Generate and download PDF
  const handleGeneratePDF = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      alert('Please fill out all required fields before generating the PDF.');
      return;
    }

    setIsGeneratingPDF(true);
    try {
      await generateFilledPDF(sessionId);
      downloadPDF(sessionId);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF: ' + error.message);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Manual save
  const handleManualSave = async () => {
    const success = await saveFormData();
    if (success) {
      alert('Form saved!');
    }
  };

  // Scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(`section-${sectionId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '20rem',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '3rem',
          height: '3rem',
          border: '3px solid #e5e7eb',
          borderTop: '3px solid #2563eb',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#6b7280' }}>Loading form...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '0.5rem',
        padding: '1rem',
        textAlign: 'center'
      }}>
        <h3 style={{ color: '#991b1b', marginBottom: '0.5rem' }}>
          Error loading form
        </h3>
        <p style={{ color: '#b91c1c', marginBottom: '1rem' }}>
          {error}
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            backgroundColor: '#2563eb',
            color: 'white',
            padding: '0.5rem 1rem',
            border: 'none',
            borderRadius: '0.25rem',
            cursor: 'pointer'
          }}
        >
          Reload page
        </button>
      </div>
    );
  }

  if (!formStructure) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
        No form data found.
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '4xl', margin: '0 auto', padding: '1rem' }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.75rem',
        border: '1px solid #e5e7eb',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', margin: 0, marginBottom: '0.25rem' }}>
              {formStructure.title}
            </h1>
            <p style={{ color: '#6b7280', margin: 0 }}>
              {formStructure.description}
            </p>
          </div>
          
          {/* Save Status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {saveStatus === 'saving' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#2563eb' }}>
                <div style={{
                  width: '1rem',
                  height: '1rem',
                  border: '2px solid #e5e7eb',
                  borderTop: '2px solid #2563eb',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <span style={{ fontSize: '0.875rem' }}>Saving...</span>
              </div>
            )}
            {saveStatus === 'saved' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#059669' }}>
                <svg style={{ width: '1rem', height: '1rem' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span style={{ fontSize: '0.875rem' }}>Saved</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '1.5rem' }}>
        {/* Form Content */}
        <div>
          {/* Action Buttons */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            border: '1px solid #e5e7eb',
            padding: '1rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => setShowAIPanel(true)}
                style={{
                  backgroundColor: '#8b5cf6',
                  color: 'white',
                  padding: '0.5rem 1rem',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#7c3aed'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#8b5cf6'}
              >
                <svg style={{ width: '1rem', height: '1rem' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                AI Fill
              </button>
              
              <button
                onClick={handleManualSave}
                disabled={!hasUnsavedChanges}
                style={{
                  backgroundColor: hasUnsavedChanges ? '#2563eb' : '#e5e7eb',
                  color: hasUnsavedChanges ? 'white' : '#9ca3af',
                  padding: '0.5rem 1rem',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: hasUnsavedChanges ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <svg style={{ width: '1rem', height: '1rem' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Save
              </button>
              
              <button
                onClick={handleGeneratePDF}
                disabled={isGeneratingPDF}
                style={{
                  backgroundColor: isGeneratingPDF ? '#e5e7eb' : '#059669',
                  color: isGeneratingPDF ? '#9ca3af' : 'white',
                  padding: '0.5rem 1rem',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: isGeneratingPDF ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => {
                  if (!isGeneratingPDF) e.target.style.backgroundColor = '#047857';
                }}
                onMouseOut={(e) => {
                  if (!isGeneratingPDF) e.target.style.backgroundColor = '#059669';
                }}
              >
                {isGeneratingPDF ? (
                  <div style={{
                    width: '1rem',
                    height: '1rem',
                    border: '2px solid #9ca3af',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                ) : (
                  <svg style={{ width: '1rem', height: '1rem' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}
                Generate PDF
              </button>
            </div>
          </div>

          {/* Form Sections */}
          {formStructure.sections.map((section) => (
            <div key={section.sectionId} id={`section-${section.sectionId}`}>
              <FormSection
                section={section}
                formData={formData}
                onChange={handleFieldChange}
                errors={validationErrors}
                visibleFieldIds={visibleFieldIds}
              />
            </div>
          ))}
        </div>

        {/* Sidebar */}
        <div style={{ position: 'sticky', top: '1rem', height: 'fit-content' }}>
          <FormProgress
            formStructure={formStructure}
            formData={formData}
            onSectionClick={scrollToSection}
            visibleFieldIds={visibleFieldIds}
          />
        </div>
      </div>

      {/* AI Fill Panel */}
      {showAIPanel && (
        <AIFillPanel
          sessionId={sessionId}
          onClose={() => setShowAIPanel(false)}
          onComplete={handleAIFillComplete}
        />
      )}

      {/* Add CSS for spin animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default DynamicFormRenderer;
